import { useQuery } from "@tanstack/react-query";
import { navigate } from "raviger";
import { createContext, useEffect, useState } from "react";

import { useAuthContext } from "@/hooks/useAuthUser";

import { LocalStorageKeys } from "@/common/constants";
import { PublicPatientRead } from "@/types/emr/patient/patient";
import publicPatientApi from "@/types/emr/patient/publicPatientApi";
import { TokenData } from "@/types/otp/otp";
import query from "@/Utils/request/query";

export type PatientUserContextType = {
  patients?: PublicPatientRead[];
  selectedPatient: PublicPatientRead | null;
  setSelectedPatient: (patient: PublicPatientRead) => void;
  tokenData: TokenData;
};

export const PatientUserContext = createContext<PatientUserContextType | null>(
  null,
);

interface Props {
  children: React.ReactNode;
}

export default function PatientUserProvider({ children }: Props) {
  const [patients, setPatients] = useState<PublicPatientRead[]>([]);
  const [selectedPatient, setSelectedPatientState] =
    useState<PublicPatientRead | null>(null);

  const { patientToken: tokenData } = useAuthContext();

  const setSelectedPatient = (patient: PublicPatientRead) => {
    setSelectedPatientState(patient);
    localStorage.setItem(LocalStorageKeys.selectedPatientKey, patient.id);
  };

  const { data: userData } = useQuery({
    queryKey: ["patients", tokenData],
    queryFn: query.paginated(publicPatientApi.list, {
      headers: {
        Authorization: `Bearer ${tokenData?.token}`,
      },
      pageSize: 100,
    }),
    enabled: !!tokenData?.token,
  });

  useEffect(() => {
    if (!userData?.results) {
      return;
    }

    setPatients(userData.results);

    if (userData.results.length === 0) {
      setSelectedPatientState(null);
      localStorage.removeItem(LocalStorageKeys.selectedPatientKey);
      return;
    }

    const storedSelectedPatientId = localStorage.getItem(
      LocalStorageKeys.selectedPatientKey,
    );
    const nextSelectedPatient =
      userData.results.find((patient) => patient.id === selectedPatient?.id) ??
      userData.results.find(
        (patient) => patient.id === storedSelectedPatientId,
      ) ??
      userData.results[0];

    if (selectedPatient?.id !== nextSelectedPatient.id) {
      setSelectedPatientState(nextSelectedPatient);
    }

    if (storedSelectedPatientId !== nextSelectedPatient.id) {
      localStorage.setItem(
        LocalStorageKeys.selectedPatientKey,
        nextSelectedPatient.id,
      );
    }
  }, [selectedPatient?.id, userData]);

  if (!tokenData) {
    navigate("/");
    return null;
  }

  return (
    <PatientUserContext.Provider
      value={{
        patients,
        selectedPatient,
        setSelectedPatient,
        tokenData: tokenData,
      }}
    >
      {children}
    </PatientUserContext.Provider>
  );
}
